/**
 * AdminDashboard — Tabbed Shell
 * DevGPT Admin Console v3.9.0
 *
 * Five-tab layout with dark sidebar, light content area.
 * Industrial-utilitarian design for Doyle Security Systems ops.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SystemHealth } from './tabs/SystemHealth';
import { DocumentPipeline } from './tabs/DocumentPipeline';
import { UsageAnalytics } from './tabs/UsageAnalytics';
import { Governance } from './tabs/Governance';
import { QuickActions } from './tabs/QuickActions';
import { SystemDiagnostics } from './tabs/SystemDiagnostics';
import { ConversationReview } from './tabs/ConversationReview';
import { ErrorBoundary } from '../common/ErrorBoundary';
import type { AdminTab } from '../../types/admin';
import './AdminDashboard.css';

interface TabConfig {
  id: AdminTab;
  label: string;
  icon: string;
}

const TABS: TabConfig[] = [
  { id: 'health', label: 'System Health', icon: '●' },
  { id: 'pipeline', label: 'Document Pipeline', icon: '⬡' },
  { id: 'analytics', label: 'Usage Analytics', icon: '▦' },
  { id: 'conversations', label: 'Conversation Review', icon: '▤' },
  { id: 'diagnostics', label: 'Diagnostics', icon: '◉' },
  { id: 'governance', label: 'Governance', icon: '◈' },
  { id: 'actions', label: 'Quick Actions', icon: '▸' },
];

const TAB_COMPONENTS: Record<AdminTab, React.FC> = {
  health: SystemHealth,
  pipeline: DocumentPipeline,
  analytics: UsageAnalytics,
  conversations: ConversationReview,
  diagnostics: SystemDiagnostics,
  governance: Governance,
  actions: QuickActions,
};

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AdminTab>('health');

  const ActiveComponent = TAB_COMPONENTS[activeTab];

  return (
    <div className="adm-root">
      {/* Sidebar */}
      <aside className="adm-sidebar">
        <div className="adm-sidebar-header">
          <div className="adm-sidebar-brand">Doyle Vault</div>
          <div className="adm-sidebar-sub">Admin Console</div>
        </div>

        <nav className="adm-sidebar-nav">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              className={`adm-nav-item ${activeTab === tab.id ? 'adm-nav-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="adm-nav-icon">{tab.icon}</span>
              <span className="adm-nav-label">{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="adm-sidebar-footer">
          <div className="adm-sidebar-version">v3.9.0</div>
          <button
            className="adm-back-btn"
            onClick={() => navigate('/chat')}
          >
            ← Back to Chat
          </button>
        </div>
      </aside>

      {/* Content Area — wrapped in ErrorBoundary so a single panel crash
          doesn't take down the whole admin console. Keyed on activeTab so
          switching tabs resets the boundary and retries the new panel. */}
      <main id="main-content" tabIndex={-1} className="adm-main">
        <ErrorBoundary
          key={activeTab}
          label={TABS.find((t) => t.id === activeTab)?.label ?? 'Panel'}
        >
          <ActiveComponent />
        </ErrorBoundary>
      </main>
    </div>
  );
};

export default AdminDashboard;
